import { FormEvent, useEffect, useState, type CSSProperties } from "react";
import { useApiFetch } from "../hooks/useApi";

type TenantRow = { id: string; name: string };
type TenantScopedRole = "customer_admin" | "campaign_operator";
type TenantUserStatus = "active" | "disabled";

interface TenantUserRow {
  tenant_id: string;
  user_id: string;
  primary_email: string | null;
  role: TenantScopedRole;
  status: TenantUserStatus;
  created_at: string;
  updated_at: string;
}

export function TenantAccessPage() {
  const api = useApiFetch();
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [tenantErr, setTenantErr] = useState<string | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<string>("");

  const [listState, setListState] = useState<
    | { status: "idle" | "loading" }
    | { status: "ok"; users: TenantUserRow[]; bootstrapMode: boolean }
    | { status: "err"; message: string }
  >({ status: "idle" });

  const [form, setForm] = useState({
    userId: "",
    primaryEmail: "",
    role: "campaign_operator" as TenantScopedRole,
    status: "active" as TenantUserStatus,
  });
  const [saveState, setSaveState] = useState<{ status: "idle" | "saving" | "ok" | "err"; message?: string }>({
    status: "idle",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api("/api/v1/tenants");
        const text = await res.text();
        if (cancelled) return;
        if (!res.ok) {
          setTenantErr(text);
          return;
        }
        const data = JSON.parse(text) as { tenants?: TenantRow[] };
        setTenants(data.tenants ?? []);
      } catch (e) {
        if (!cancelled) setTenantErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  async function loadTenantUsers(tenantId: string) {
    if (!tenantId) {
      setListState({ status: "idle" });
      return;
    }
    try {
      setListState({ status: "loading" });
      const path = `/api/v1/platform/tenants/${encodeURIComponent(tenantId)}/users`;
      const res = await api(path);
      const text = await res.text();
      if (!res.ok) {
        let detail = text;
        try {
          const j = JSON.parse(text) as { error?: string; detail?: string };
          detail = j.detail ?? j.error ?? text;
        } catch {
          /* raw */
        }
        setListState({ status: "err", message: detail });
        return;
      }
      const payload = JSON.parse(text) as { users?: TenantUserRow[]; bootstrap_mode?: boolean };
      setListState({
        status: "ok",
        users: payload.users ?? [],
        bootstrapMode: payload.bootstrap_mode === true,
      });
    } catch (e) {
      setListState({ status: "err", message: e instanceof Error ? e.message : String(e) });
    }
  }

  useEffect(() => {
    void loadTenantUsers(selectedTenant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!selectedTenant) return;
    setSaveState({ status: "saving" });
    try {
      const path = `/api/v1/platform/tenants/${encodeURIComponent(selectedTenant)}/users`;
      const res = await api(path, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          user_id: form.userId.trim(),
          primary_email: form.primaryEmail.trim() || null,
          role: form.role,
          status: form.status,
        }),
      });
      const text = await res.text();
      if (!res.ok) {
        let detail = text;
        try {
          const j = JSON.parse(text) as { error?: string; detail?: string };
          detail = j.detail ?? j.error ?? text;
        } catch {
          /* raw */
        }
        setSaveState({ status: "err", message: detail });
        return;
      }
      setSaveState({ status: "ok", message: "Saved." });
      await loadTenantUsers(selectedTenant);
    } catch (err) {
      setSaveState({ status: "err", message: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <div>
      <p className="sd-kicker">Tenant RBAC</p>
      <h1 className="sd-h1">Tenant access</h1>
      <p className="sd-lead">
        Assign Clerk users to a tenant as <strong>customer_admin</strong> or <strong>campaign_operator</strong> (
        <code>tenant_user_roles</code>). Tenant APIs will enforce these roles in later phases; platform operators manage
        assignments here first.
      </p>

      {tenantErr ? (
        <div className="sd-main-card" style={{ color: "var(--sd-danger)", marginBottom: "1rem" }}>
          Could not load tenants: {tenantErr}
        </div>
      ) : null}

      <div className="sd-main-card" style={{ marginBottom: "1rem" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem", fontSize: "0.85rem" }}>
          Tenant
          <select
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
            style={inputStyle}
          >
            <option value="">Select tenant…</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.id})
              </option>
            ))}
          </select>
        </label>
      </div>

      {selectedTenant ? (
        <>
          <div className="sd-main-card" style={{ marginBottom: "1rem" }}>
            <form
              onSubmit={(e) => void onSubmit(e)}
              style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0.75rem" }}
            >
              <label style={labelStyle}>
                Clerk user ID
                <input
                  value={form.userId}
                  onChange={(e) => setForm((f) => ({ ...f, userId: e.target.value }))}
                  required
                  style={inputStyle}
                />
              </label>
              <label style={labelStyle}>
                Primary email (optional)
                <input
                  value={form.primaryEmail}
                  onChange={(e) => setForm((f) => ({ ...f, primaryEmail: e.target.value }))}
                  style={inputStyle}
                />
              </label>
              <label style={labelStyle}>
                Role
                <select
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as TenantScopedRole }))}
                  style={inputStyle}
                >
                  <option value="customer_admin">customer_admin</option>
                  <option value="campaign_operator">campaign_operator</option>
                </select>
              </label>
              <label style={labelStyle}>
                Status
                <select
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as TenantUserStatus }))}
                  style={inputStyle}
                >
                  <option value="active">active</option>
                  <option value="disabled">disabled</option>
                </select>
              </label>
              <div style={{ display: "flex", alignItems: "end" }}>
                <button type="submit" style={buttonStyle} disabled={saveState.status === "saving"}>
                  {saveState.status === "saving" ? "Saving…" : "Save assignment"}
                </button>
              </div>
            </form>
            {saveState.status === "err" ? (
              <p style={{ margin: "0.75rem 0 0", color: "var(--sd-danger)", fontSize: "0.85rem" }}>{saveState.message}</p>
            ) : null}
            {saveState.status === "ok" ? (
              <p style={{ margin: "0.75rem 0 0", color: "var(--sd-muted)", fontSize: "0.85rem" }}>{saveState.message}</p>
            ) : null}
          </div>

          <div className="sd-main-card" style={{ padding: 0, overflow: "hidden" }}>
            {listState.status === "loading" || listState.status === "idle" ? (
              <div style={{ padding: "1rem 1.25rem", color: "var(--sd-muted)" }}>Loading…</div>
            ) : null}
            {listState.status === "err" ? (
              <div style={{ padding: "1rem 1.25rem", color: "var(--sd-danger)" }}>{listState.message}</div>
            ) : null}
            {listState.status === "ok" ? (
              <>
                {listState.bootstrapMode ? (
                  <div style={{ padding: "0.9rem 1.25rem", fontSize: "0.85rem", color: "var(--sd-muted)" }}>
                    Platform bootstrap mode: role enforcement on tenant APIs is still coming; assignments are stored in D1
                    now.
                  </div>
                ) : null}
                {listState.users.length === 0 ? (
                  <div style={{ padding: "1rem 1.25rem", color: "var(--sd-muted)" }}>No users assigned to this tenant yet.</div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                    <thead>
                      <tr style={{ textAlign: "left", borderBottom: "1px solid var(--sd-border)", color: "var(--sd-muted)" }}>
                        <th style={thStyle}>User ID</th>
                        <th style={thStyle}>Email</th>
                        <th style={thStyle}>Role</th>
                        <th style={thStyle}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listState.users.map((u) => (
                        <tr key={u.user_id} style={{ borderBottom: "1px solid var(--sd-border)" }}>
                          <td style={tdMonoCell}>{u.user_id}</td>
                          <td style={tdCell}>{u.primary_email ?? "—"}</td>
                          <td style={tdCell}>{u.role}</td>
                          <td style={tdCell}>{u.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            ) : null}
          </div>
        </>
      ) : (
        <p style={{ color: "var(--sd-muted)", fontSize: "0.9rem" }}>Select a tenant to view or edit assignments.</p>
      )}
    </div>
  );
}

const labelStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.35rem",
  fontSize: "0.85rem",
};

const inputStyle: CSSProperties = {
  background: "var(--sd-surface)",
  border: "1px solid var(--sd-border)",
  color: "var(--sd-text)",
  borderRadius: "8px",
  padding: "0.5rem 0.6rem",
  font: "inherit",
};

const buttonStyle: CSSProperties = {
  background: "var(--sd-accent)",
  color: "#04100e",
  border: "none",
  borderRadius: "8px",
  padding: "0.55rem 0.9rem",
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};

const thStyle: CSSProperties = { padding: "0.7rem 1rem", fontWeight: 600 };
const tdCell: CSSProperties = { padding: "0.65rem 1rem" };
const tdMonoCell: CSSProperties = {
  padding: "0.65rem 1rem",
  fontFamily: "var(--sd-mono)",
  fontSize: "0.8rem",
};
